import { Component, inject, Input } from '@angular/core';

import { OrderData, OrderState } from '@lfw/roesberg.liveforms.core';
import { RBConvert, ViewModelProperty, VmpFieldType, VMPFormGroupComponent } from '@lfw/roesberg';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lfw-order-details',
  imports: [FormsModule, VMPFormGroupComponent, ReactiveFormsModule],
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.css'
})
export class OrderDetailsComponent {
  form: UntypedFormGroup = new UntypedFormGroup({});

  @Input() captionWidth = 230;
  @Input() valueWidth = 600;
  @Input() set order(value: OrderData | undefined) {
    if (value === this.orderData) {
      return;
    }
    this.orderData = value;
    this.updateVMPData();
  }

  private orderData: OrderData | undefined;
  private translate = inject(TranslateService);

  groupData = new Array<ViewModelProperty>();
  groupProcess = new Array<ViewModelProperty>();

  isLoaded(): boolean {
    return this.orderData !== undefined;
  }

  hasProcessData(): boolean {
    return this.orderData?.orderState.state === OrderState.InProcess;
  }

  private updateVMPData() {
    this.groupData = [];
    this.groupProcess = [];
    if (this.orderData === undefined) {
      return;
    }

    const orderGroupText = this.translate.instant('ORDERS.DETAILS.GROUPTEXT.ORDERSTEXT');
    this.groupData = this.createDataGroup([
      { groupText: orderGroupText, captionKey: 'ORDERS.DETAILS.NUMBER', value: this.orderData?.text },
      {
        groupText: orderGroupText,
        captionKey: 'ORDERS.DETAILS.DUEDATE',
        value: RBConvert.jsonUTCStringToString(this.orderData?.completionDate)
      },
      { groupText: orderGroupText, captionKey: 'ORDERS.DETAILS.LIVEFORM', value: this.orderData?.virtualLiveFormName },
      { groupText: orderGroupText, captionKey: 'ORDERS.DETAILS.ASSET', value: this.orderData?.assetName },
      { groupText: orderGroupText, captionKey: 'ORDERS.DETAILS.SCENARIO', value: this.orderData?.scenario?.name ?? '' },
      {
        groupText: orderGroupText,
        captionKey: 'ORDERS.DETAILS.STATUS',
        value: this.orderData?.orderState.text,
        style: this.orderData?.orderState.getStyle() ?? ''
      },
      {
        groupText: orderGroupText,
        captionKey: 'ORDERS.DETAILS.LASTEDITUSER',
        value: this.orderData?.lastEditUserName ?? ''
      }
    ]);

    if (this.hasProcessData()) {
      const processGroupText = this.translate.instant('ORDERS.DETAILS.GROUPTEXT.ACTUALUSER');
      this.groupProcess = this.createDataGroup([
        { groupText: processGroupText, captionKey: 'ORDERS.DETAILS.NAME', value: this.orderData?.activeUserName },
        {
          groupText: processGroupText,
          captionKey: 'ORDERS.DETAILS.INPROGRESSSINCE',
          value: RBConvert.jsonUTCStringToString(this.orderData?.userSince)
        }
      ]);
    }
  }

  private createDataGroup(
    fields: Array<{ groupText: string; captionKey: string; value: any; style?: string }>
  ): Array<ViewModelProperty> {
    return fields.map((f) => this.CreateVMP(f.groupText, this.translate.instant(f.captionKey), f.value, f.style ?? ''));
  }

  private CreateVMP(groupName: string, caption: string, value: any, style?: string): ViewModelProperty {
    const vmp = new ViewModelProperty();
    vmp.groupName = '';
    vmp.order = 1;
    vmp.subGroupName = groupName;
    vmp.caption = caption;
    vmp.fieldType = VmpFieldType.StaticText;
    vmp.fieldName = 'VMP' + groupName + caption;
    vmp.readOnly = true;
    vmp.displayValue = value;
    vmp.value = value;
    vmp.style = style ?? '';
    return vmp;
  }
}
